
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { BookingForm } from "@/components/BookingForm";
import { BookingImageCarousel } from "@/components/BookingImageCarousel";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const Booking = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Update to use classId parameter consistently
  const classId = searchParams.get("classId") || searchParams.get("class") || "";
  
  // Use useEffect for navigation instead of redirecting during render
  useEffect(() => {
    if (!classId) {
      navigate("/classes");
    }
  }, [classId, navigate]);
  
  // If no classId, render a loading state until redirect happens
  if (!classId) {
    return <div className="flex justify-center items-center min-h-screen">{t('common.loading')}</div>;
  }
  
  // Fix for expectations array handling
  const expectations = t('booking.expectations', { returnObjects: true });
  const expectationsArray = Array.isArray(expectations) ? expectations : [];
  
  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container max-w-7xl mx-auto px-6">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-8 flex items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('booking.backToClasses')}
          </Button>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-3 py-1 text-xs font-medium bg-clay-100 text-clay-800 rounded-full mb-4">
              {t('booking.title')}
            </span>
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">{t('booking.title')}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('booking.subtitle')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:block"
            >
              <div className="sticky top-24 space-y-8">
                <BookingImageCarousel />
                
                <div className="mt-8 bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-display font-medium mb-4">{t('booking.whatToExpect')}</h3>
                  <ul className="space-y-3">
                    {expectationsArray.map((item: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="w-5 h-5 bg-primary/20 text-primary flex items-center justify-center rounded-full mr-3 flex-shrink-0 mt-0.5">
                          ✓
                        </span>
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-card border border-border rounded-xl p-6 md:p-8">
                <BookingForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Booking;
